import Props from 'models/Properties'
import { AccountRoles } from '../components/AccountRoles';

const PropsTemplate = [
    {
        _group: {
            title: ""
        },
        items: [
            {
                path: 'email',
                title: 'Email',
                type: 'string',
                readOnly: true
            },
            {
                path: 'display_name',
                title: 'Display Name',
                type: 'string'
            }
        ]
    }
];

class DimensionRecord
{
    readonly rawData = {};

    own: boolean = false;
    admin: boolean = false;
    edit: boolean = false;
    view: boolean = false;
    enabled: boolean = true;
    favorite: boolean = false;
    env: string = '';
    role: AccountRoles | undefined = undefined;

    constructor(data: any) {
        if (!data) {
            return;
        }
        this.rawData = data;

        this.own = !!data.own;
        this.admin = !!data.admin;
        this.edit = !!data.edit;
        this.view = !!data.view;
        this.enabled = data.hasOwnProperty('enabled') ? (data.enabled  ? true : false) : this.enabled;
        this.favorite = !!data.favorite;
        this.env = data.env;
        if (data?.role && Object.values(AccountRoles).includes(data.role)) {
            this.role = data.role;
        }
    }

    valid() {
        return this.own || this.admin || this.edit || this.view || this.favorite || this.enabled || this.env || this.role;
    }

    isEqual(other: DimensionRecord | undefined) {
        if (!other) {
            return false
        }

        return this.own === other.own &&
               this.own === other.own &&
               this.admin === other.admin &&
               this.edit === other.edit &&
               this.view === other.view &&
               this.enabled === other.enabled &&
               this.favorite === other.favorite &&
               this.env === other.env;
    }

    getJsonRecord() {
        let record: {[id: string] : any} = {};
        if (this.own) { record['own'] = true; }
        if (this.admin) { record['admin'] = true; }
        if (this.edit) { record['edit'] = true; }
        if (this.view) { record['view'] = true; }
        record['enabled'] = this.enabled;
        if (this.favorite) { record['favorite'] = true; }
        if (this.role) { record['role'] = this.role; }
        record['env'] = this.env;
        return record;
    }
}

type DimensionRecords = {[id: string] : DimensionRecord};

type StudioInfo = {
    enabled: boolean;
    maxDimensions: number;
    canListPublic: boolean;
    canEditCode: boolean;
}

function isStudioInfoEqual(a: StudioInfo, b: StudioInfo) {
    return a.enabled === b.enabled &&
           a.maxDimensions === b.maxDimensions &&
           a.canListPublic === b.canListPublic &&
           a.canEditCode === b.canEditCode;
}

function isDimensionsInfoEqual(a: DimensionRecords, b: DimensionRecords) {
    const keys1 = Object.keys(a);
    const keys2 = Object.keys(b);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!a[key].isEqual(b[key])) {
            return false;
        }
    }

    return true;
}

class Account
{
    rawData: any = {};
    readonly id: string = '';
    readonly email: string = '';
    displayName: string = '';
    readonly settings: boolean = false;
    readonly developer: boolean = false;

    readonly studio: StudioInfo = {
        enabled: false,
        maxDimensions: 1,
        canListPublic: false,
        canEditCode: false
    };

    dimensions: DimensionRecords = {};

    properties: any[] = [];

    constructor(data: any) {
        if (!data) {
            return;
        }
        this.rawData = data;
        this.id = data.id;
        this.email = data.email;
        if (data.display_name) {
            this.displayName = data.display_name;
        }
        this.settings = data.settings;
        this.developer = data.developer;
        if (this.developer) {
            this.settings = true;
        }

        if (data.studio) {
            if (data.studio.enabled !== undefined) {
                this.studio.enabled = data.studio.enabled;
            }
            if (data.studio.max_dimensions !== undefined) {
                this.studio.maxDimensions = data.studio.max_dimensions;
            }
            if (data.studio.can_list_public !== undefined) {
                this.studio.canListPublic = data.studio.can_list_public;
            }
            if (data.studio.can_edit_code !== undefined) {
                this.studio.canEditCode = data.studio.can_edit_code;
            }
        }

        for (let dimId in data.dimensions) {
            this.dimensions[dimId] = new DimensionRecord(data.dimensions[dimId]);
        }

        this.properties = Props.parse(PropsTemplate, this.rawData);
    }

    valid() {
        return this.id && this.id.length > 0;
    }

    isEqual(other: Account | undefined) {
        if (!other) {
            return false;
        }

        if (this.id != other.id || this.email != other.email || this.displayName != other.displayName) {
            return false;
        }

        if (!isStudioInfoEqual(this.studio, other.studio)) {
            return false;
        }

        if (!isDimensionsInfoEqual(this.dimensions, other.dimensions)) {
            return false;
        }

        return true;
    }

    updateProperties(props: any) {
        if ('display_name' in props) {
            this.displayName = props.display_name;
        }
        // simply merge
        this.rawData = {...this.rawData, ...props}
    }

    markFavorite(dimId: string, val: boolean) {
        this.tryCreateRecord(dimId);
        this.dimensions[dimId].favorite = val;
        //this.validateDimensionRecord(dimId);
        this.rawData['dimensions'] = this.getDimensionsRecords();
    }

    enableDimension(dimId: string, val: boolean) {
        this.tryCreateRecord(dimId);
        this.dimensions[dimId].enabled = val;
        //this.validateDimensionRecord(dimId);
        this.rawData['dimensions'] = this.getDimensionsRecords();
    }

    setDimensionEnv(dimId: string, val: string) {
        this.tryCreateRecord(dimId);
        this.dimensions[dimId].env = val;
        //this.validateDimensionRecord(dimId);
        this.rawData['dimensions'] = this.getDimensionsRecords();
    }

    getDimensionEnv(dimId: string) {
        let record = this.dimensions[dimId];
        return record && record.env ? record.env : 'prod';
    }

    isDimensionAdmin(dimId: string) {
        let record = this.dimensions[dimId];
        return record ? record.own || record.role === AccountRoles.Admin : false;
    }

    canEditDimension(dimId: string) {
        let record = this.dimensions[dimId];
        if (!record) {
            return false;
        }
        return record.own || record.admin || record.edit || record.role === AccountRoles.Admin || record.role === AccountRoles.Editor;
    }

    getDimensionsRecords() {
        let records: {[id: string] : any} = {};
        for (let dimId in this.dimensions) {
            records[dimId] = this.dimensions[dimId].getJsonRecord();
        }
        return records;
    }

    getEditableDimensions() {
        return Object.keys(this.dimensions).filter(id => this.canEditDimension(id))
    }

    getAllDimensions() {
        return Object.keys(this.dimensions);
    }

    private tryCreateRecord(dimId: string) {
        if (!(dimId in this.dimensions)) {
            this.dimensions[dimId] = new DimensionRecord(null);
        }
    }
/*
    private validateDimensionRecord(dimId: string) {
        if (dimId in this.dimensions &&
            !this.dimensions[dimId].valid())
        {
            delete this.dimensions[dimId];
        }
    }
*/

}

export default Account;
